import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  AuthorizedButton,
  Container,
  DataTable,
  ReconciliationModal,
  PageTitle,
  SExportButton,
} from '../../components';
import { tableActions } from '../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../store';
import useHttpService from '../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import {
  IFile,
  IReconciliationDataSource,
  IReconciliationInitialValues,
} from '../../types';
import { modalTypes } from '../../assets/enums';
import { IEnum } from '../../services/type';
import { downloadFile } from '../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../utils/functions';
import useFetchParameters from '../../hooks';
import ReconciliationService from '../../api-clients/ReconciliationService';
import EnumService from '../../api-clients/EnumService';

const Reconciliation: FC = () => {
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  const dispatch = useAppDispatch();
  const executeService = useHttpService();

  // State
  const [selectedItem, setSelectedItem] = useState<
    IReconciliationDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IReconciliationDataSource[]>([]);
  const [currentAccounts, setCurrentAccounts] = useState<IEnum[]>([]);
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);

  const parameters = useFetchParameters({ search, sort, page });
  const reconciliationService = useMemo(
    () => new ReconciliationService(executeService, parameters),
    [executeService, parameters],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchEnums = async () => {
    const response = await enumService.fetchCurrentAccounts();
    if (response && Array.isArray(response)) {
      setCurrentAccounts(response);
    }
  };

  const handleFetchData = async () => {
    const response = await reconciliationService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<IReconciliationDataSource[]>(
        (prev: IReconciliationDataSource[], cur: IReconciliationDataSource) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await reconciliationService.fetchItemById(id);
    selectedItem && 'id' in selectedItem && setSelectedItem(selectedItem);
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleClickDelete = async (id: number) => {
    try {
      await reconciliationService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IReconciliationDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 10 }));
    }
  };

  const handleClickExport = async () => {
    const response = await reconciliationService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IReconciliationInitialValues) => {
    try {
      await reconciliationService.submitForm(values, selectedItem);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IReconciliationDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await reconciliationService.deleteFile(id ?? additionalFileID!, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await reconciliationService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleOpenEditModal,
    handleClickDelete,
    handleSetAdditionalFile,
    organizations,
    currentAccounts,
  );

  // Form Management
  const formik = useFormik<IReconciliationInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IReconciliationDataSource,
        IReconciliationInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Muhasebe" />
      <Flex vertical gap="middle">
        <Flex justify="flex-end" gap={5}>
          <SExportButton onClick={handleClickExport} />
          <AuthorizedButton
            model="reconciliation"
            action="add"
            className={styles.button}
            onClick={handleOpenModal}
          >
            <PlusOutlined className={styles.buttonIcon} />
            <div className={styles.buttonText}>Yeni Kayıt Ekle</div>
          </AuthorizedButton>
        </Flex>
        <DataTable<IReconciliationDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
        />
      </Flex>
      <ReconciliationModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Muhasebe Kayıt Ekleme"
        subTitle="Muhasebe Kayıt Ekleme"
        formik={formik}
        organizations={organizations}
        currentAccounts={currentAccounts}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
      />
      <ReconciliationModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Muhasebe Kayıt Güncelleme"
        subTitle="Muhasebe Kayıt Güncelleme"
        formik={formik}
        organizations={organizations}
        currentAccounts={currentAccounts}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="reconciliationadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default Reconciliation;
